import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';
import { useRef } from 'react';

import { CONTAINER, CTA_UTILS, EYEBROW, EYEBROW_MARK, H2, LEAD, SECTION_PAD, SHADOW_MD } from '../classes';
import { FORM_ID, IMG, m } from '../shared';
import { ClientVitals } from './client-vitals';

// Feature names stay canonical in every locale.
const FEATURES = ['FPS Boost', 'Aim Trainer', 'AudioPad', 'Replay X', 'Pro Settings', 'Multi Internet'];

const SHOT = 'relative aspect-video overflow-hidden border border-[#93FF18]/[.16] bg-[#0A0B0A] forced-colors:border-[CanvasText] [&_img]:absolute [&_img]:inset-0 [&_img]:block [&_img]:size-full [&_img]:max-w-none [&_img]:object-cover';

const THUMBS = [
  { slug: 'servers', alt: 'lp.download.serversAlt', caption: 'lp.download.serversCaption' },
  { slug: 'ping', alt: 'lp.download.pingAlt', caption: 'lp.download.pingCaption' },
  { slug: 'fpsboost', alt: 'lp.download.fpsAlt', caption: 'lp.download.fpsCaption' },
];

const WindowsIcon = () => (
  <svg className='size-[1.125rem] flex-none' viewBox='0 0 50 50' fill='currentColor' aria-hidden='true' focusable='false'>
    <path d='M0 7.07917L20.4334 4.30504L20.4426 23.954L0.0188949 24.0699L0 7.07917ZM20.4237 26.2178L20.4397 45.8839L0.0160321 43.0847L0.0148869 26.086L20.4237 26.2178ZM22.9007 3.94201L49.9937 0V23.704L22.9007 23.9186V3.94201ZM50 26.4028L49.9937 50L22.9007 46.1881L22.8629 26.3588L50 26.4028Z' />
  </svg>
);

interface DownloadProps {
  onTrialClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  downloadUrl: string;
  onDownloadClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export function Download({ onTrialClick, downloadUrl, onDownloadClick }: DownloadProps) {
  const { t } = useTranslation('pages/trial-matrix');
  const cropRef = useRef<HTMLDivElement>(null);
  const shotRef = useRef<HTMLImageElement>(null);

  return (
    <section className={`${SECTION_PAD} bg-[linear-gradient(180deg,#070A08,#0B110D_50%,#070A08)]`} id='baixar' aria-labelledby='dl-t'>
      <div className={CONTAINER}>
        <div className='grid grid-cols-[minmax(0,1fr)] items-center gap-[clamp(2rem,1rem+3vw,3.5rem)] min-[992px]:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] min-[1344px]:gap-[clamp(3.5rem,1rem+4vw,5.5rem)]'>
          <div className='min-w-0' data-reveal=''>
            <p className={EYEBROW}>
              <span className={EYEBROW_MARK} aria-hidden='true'>
                &gt;
              </span>{' '}
              {t('lp.download.eyebrow')}
            </p>
            <h2 className={H2} id='dl-t'>
              <Trans t={t} i18nKey='lp.download.title' components={{ em: <em /> }} />
            </h2>
            <p className={LEAD}>{t('lp.download.lead')}</p>

            <ul className='mt-6 flex max-w-sm flex-wrap gap-2'>
              {FEATURES.map((feature) => (
                <li key={feature} className='rounded-full border border-[#2C3B31] bg-[#93FF18]/[.05] px-[.7rem] py-[.35rem] font-mono text-[.75rem] leading-[1.3333] tracking-[.04em] text-[#CBD5E1] forced-colors:border-[CanvasText]'>
                  {feature}
                </li>
              ))}
            </ul>

            <div className='mt-6 flex flex-wrap gap-3'>
              <a className={`${m('btn-primary cta')} ${CTA_UTILS}`} href={`#${FORM_ID}`} onClick={onTrialClick}>
                {t('lp.cta')}
              </a>
              <a className={`${m('btn btn-secondary')} min-h-[3.25rem] gap-2 rounded-xl`} href={downloadUrl} download onClick={onDownloadClick}>
                <WindowsIcon />
                {t('lp.download.downloadButton')}
              </a>
            </div>
            <p className='mt-4 text-[.875rem] leading-[1.4286] text-[#9AA3B5] max-sm:text-[.9375rem]'>{t('lp.download.noCard')}</p>

            <div className='mt-5 inline-flex flex-wrap items-center gap-x-4 gap-y-2 rounded-full border border-[#2C3B31] bg-[rgb(12_18_13/.7)] px-[.85rem] py-[.45rem] forced-colors:border-[CanvasText]'>
              <span className='whitespace-nowrap font-mono text-[.6875rem] uppercase leading-[1.2] tracking-[.12em] text-[#9AA3B5]'>{t('lp.download.certified')}</span>
              <ul className='flex items-center gap-4'>
                <li className='flex items-center'>
                  <picture>
                    <source type='image/avif' srcSet={`${IMG}/cert-microsoft-96.avif`} />
                    <img className='block h-[1.65rem] w-auto opacity-[.95] brightness-150 contrast-[1.1] grayscale forced-colors:filter-none' src={`${IMG}/cert-microsoft-96.webp`} alt='Microsoft' width={96} height={99} loading='lazy' decoding='async' />
                  </picture>
                </li>
                <li className='flex items-center border-s border-[#2C3B31] ps-4'>
                  <picture>
                    <source type='image/avif' srcSet={`${IMG}/cert-digicert-128.avif`} />
                    <img className='block h-[1.15rem] w-auto brightness-[.6] grayscale forced-colors:filter-none' src={`${IMG}/cert-digicert-128.webp`} alt='DigiCert' width={128} height={52} loading='lazy' decoding='async' />
                  </picture>
                </li>
              </ul>
            </div>
          </div>

          <div className="relative grid w-full min-w-0 max-w-[46rem] grid-cols-[minmax(0,1fr)] gap-[clamp(.75rem,.55rem+.6vw,1rem)] before:pointer-events-none before:absolute before:inset-x-0 before:inset-y-[-8%] before:z-0 before:bg-[radial-gradient(66%_55%_at_50%_45%,rgb(147_255_24/.15),transparent_70%)] before:content-[''] min-[992px]:justify-self-end forced-colors:before:hidden" data-reveal=''>
            <figure className='relative z-[1] m-0 min-w-0 rounded-3xl border border-[#2C3B31] bg-[linear-gradient(180deg,rgb(18_26_19/.94),rgb(8_12_9/.96))] p-3 shadow-[0_40px_100px_rgb(0_0_0/.6),0_16px_40px_rgb(0_0_0/.4),0_0_90px_-38px_rgb(147_255_24/.8)]' id='app-shot'>
              <div className='flex items-center gap-3 px-1 pb-3 pt-0.5 font-mono text-[.6875rem] uppercase tracking-[.06em] text-[#9AA3B5]'>
                <span className='inline-flex flex-none gap-[.3rem]' aria-hidden='true'>
                  <i className='size-[7px] rounded-full bg-[#93FF18]/[.55]' />
                  <i className='size-[7px] rounded-full bg-white/[.16]' />
                  <i className='size-[7px] rounded-full bg-white/[.16]' />
                </span>
                <span className='min-w-0 truncate'>
                  NoPing<span className='max-[480px]:hidden'>{t('lp.download.windowSub')}</span>
                </span>
                <span className='ms-auto inline-flex flex-none items-center gap-[.35rem] text-[#93FF18]'>
                  <i className='size-1.5 rounded-full bg-[#93FF18] shadow-[0_0_8px_rgb(147_255_24/.9)]' aria-hidden='true' />
                  {t('lp.download.connected')}
                </span>
              </div>
              <div ref={cropRef} className={`${SHOT} rounded-xl`}>
                <picture>
                  <source type='image/avif' srcSet={`${IMG}/app-home-800.avif 800w, ${IMG}/app-home-1200.avif 1200w, ${IMG}/app-home-1600.avif 1600w`} sizes='(min-width: 62em) min(46rem, 52vw), calc(100vw - 4rem)' />
                  <img ref={shotRef} src={`${IMG}/app-home-1200.webp`} srcSet={`${IMG}/app-home-800.webp 800w, ${IMG}/app-home-1200.webp 1200w, ${IMG}/app-home-1600.webp 1600w`} sizes='(min-width: 62em) min(46rem, 52vw), calc(100vw - 4rem)' alt={t('lp.download.homeAlt')} width={1600} height={900} loading='lazy' decoding='async' />
                </picture>
                <ClientVitals cropRef={cropRef} imgRef={shotRef} />
              </div>
            </figure>

            <ul className='relative z-[1] grid grid-cols-3 gap-[clamp(.5rem,.3rem+.6vw,.875rem)]'>
              {THUMBS.map((thumb) => (
                <li key={thumb.slug} className='flex min-w-0'>
                  <figure className={`m-0 flex w-full min-w-0 flex-col gap-2 rounded-2xl border border-[#2C3B31] bg-[linear-gradient(180deg,rgb(16_23_17/.9),rgb(9_13_10/.94))] px-2 pb-2.5 pt-2 ${SHADOW_MD} max-[479.84px]:gap-[.4rem] max-[479.84px]:p-1.5 max-[479.84px]:pb-2 forced-colors:border-[CanvasText]`}>
                    <div className={`${SHOT} rounded-lg`}>
                      <picture>
                        <source type='image/avif' srcSet={`${IMG}/app-${thumb.slug}-400.avif 400w, ${IMG}/app-${thumb.slug}-800.avif 800w`} sizes='(min-width: 62em) 15rem, 31vw' />
                        <img src={`${IMG}/app-${thumb.slug}-400.webp`} srcSet={`${IMG}/app-${thumb.slug}-400.webp 400w, ${IMG}/app-${thumb.slug}-800.webp 800w`} sizes='(min-width: 62em) 15rem, 31vw' alt={t(thumb.alt)} width={800} height={450} loading='lazy' decoding='async' />
                      </picture>
                    </div>
                    <figcaption className='text-balance px-0.5 text-[.75rem] font-semibold leading-[1.35] text-white [overflow-wrap:anywhere] md:text-[.875rem]'>{t(thumb.caption)}</figcaption>
                  </figure>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
